import React, { useCallback, useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { motion } from 'framer-motion';
import { getFunctions, httpsCallable, FunctionsError } from 'firebase/functions';
import { db } from '@/config/firebase.config';
import { doc, onSnapshot } from 'firebase/firestore';
import { formatarMoeda } from '@/utils/currencyUtils';

interface PixPaymentProps {
  userId: string;
  valor: number;
  merchantInfo: { nome: string; chavePix: string; cidade: string; };
  onClose?: () => void;
  onSuccess?: (transactionId: string) => void;
}

interface PaymentState {
  status: 'idle' | 'requesting' | 'pending' | 'validating' | 'completed' | 'failed' | 'expired';
  transactionId?: string;
  error?: string;
  countdown?: number;
  provider?: 'santander' | 'mercadopago';
  qrCode?: string;
  qrCodeBase64?: string;
  ticketUrl?: string;
  originalTimestamp?: number;
  timestamp?: number;
}

interface CachedPayment {
  state: PaymentState;
  pixPayload: string;
  originalValor: number;
  merchantInfo: any;
  timestamp: number;
}

// =================================================================================================
// UTILITÁRIOS PIX E CACHE - CORRIGIDOS
// =================================================================================================

const getStorageKey = (userId: string) => `pending_pix_transaction_${userId}`;

const saveToCache = (userId: string, data: CachedPayment) => {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(data));
  } catch (error) {
    console.warn('⚠️ Erro ao salvar cache:', error);
  }
};

const loadFromCache = (userId: string): CachedPayment | null => {
  try {
    const cached = localStorage.getItem(getStorageKey(userId));
    if (!cached) {
      return null;
    }

    const rawData = JSON.parse(cached);

    const data: CachedPayment = {
      state: {
        status: rawData.state?.status || rawData.paymentStatus || 'idle',
        transactionId: rawData.state?.transactionId || rawData.transactionId,
        error: rawData.state?.error || rawData.error,
        countdown: rawData.state?.countdown || rawData.countdown,
        timestamp: rawData.state?.timestamp || rawData.timestamp
      },
      pixPayload: rawData.pixPayload || '',
      originalValor: rawData.originalValor || rawData.valor || 0,
      merchantInfo: rawData.merchantInfo,
      timestamp: rawData.timestamp || Date.now()
    };

    // Verifica se o cache ainda é válido (menos de 10 minutos)
    const now = Date.now();
    const cacheAge = now - data.timestamp;
    const MAX_CACHE_AGE = 10 * 60 * 1000; // 10 minutos

    if (cacheAge > MAX_CACHE_AGE) {
      localStorage.removeItem(getStorageKey(userId));
      return null;
    }

    // CORREÇÃO CRÍTICA: Ajusta o countdown baseado no tempo real passado
    if (data.state.countdown && data.state.timestamp &&
      (data.state.status === 'pending' || data.state.status === 'validating')) {

      const secondsPassed = Math.floor(cacheAge / 1000);
      const newCountdown = Math.max(0, data.state.countdown - secondsPassed);

      data.state.countdown = newCountdown;

      if (newCountdown <= 0) {
        data.state.status = 'expired';
        data.state.error = 'Tempo para pagamento expirou';
      }
    }


    return data;
  } catch (error) {
    console.warn('⚠️ Erro ao carregar cache:', error);
    return null;
  }
};

const clearCache = (userId: string) => {
  try {
    localStorage.removeItem(getStorageKey(userId));
  } catch (error) {
    console.warn('⚠️ Erro ao limpar cache:', error);
  }
};

const generateTransactionId = (userId: string): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  const userPrefix = userId.substring(0, 8);
  return `pix_${userPrefix}${timestamp}${random}`.toLowerCase().substring(0, 25);
};

// =================================================================================================
// COMPONENTE PRINCIPAL - CORRIGIDO
// =================================================================================================

const PixPayment: React.FC<PixPaymentProps> = ({
  userId,
  valor,
  merchantInfo,
  onClose,
  onSuccess
}) => {
  // Estado do pagamento
  const [paymentState, setPaymentState] = useState<PaymentState>({
    status: 'idle'
  });

  const [pixPayload, setPixPayload] = useState<string>('');
  const [effectiveValor, setEffectiveValor] = useState<number>(0);

  // Refs para controle de estado assíncrono
  const paymentStateRef = useRef(paymentState);
  const cleanupCalledRef = useRef(false);
  const hasRestoredFromCacheRef = useRef(false);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Atualiza ref sempre que o estado muda
  useEffect(() => {
    paymentStateRef.current = paymentState;
  }, [paymentState]);

  // Cloud Functions
  const functions = getFunctions();
  const initiatePaymentFunc = httpsCallable(functions, 'initiatePixPayment');
  const checkPaymentFunc = httpsCallable(functions, 'checkPixPaymentStatus');
  const cancelPaymentFunc = httpsCallable(functions, 'cancelPixPayment');

  const [isCopied, setIsCopied] = useState(false);

  // =============================================================================================
  // CORREÇÃO CRÍTICA: Effect ÚNICO para carregamento inicial - executa apenas uma vez
  // =============================================================================================
  useEffect(() => {

    const cached = loadFromCache(userId);

    if (cached) {

      // CORREÇÃO CRÍTICA: Restaura TODO o estado do cache imediatamente
      setPaymentState(cached.state);
      setPixPayload(cached.pixPayload);
      setEffectiveValor(cached.originalValor);
      hasRestoredFromCacheRef.current = true;

    } else {
      setEffectiveValor(valor);
      hasRestoredFromCacheRef.current = false;
    }
  }, []); // CORREÇÃO: Array vazio - executa apenas no mount

  // =============================================================================================
  // CORREÇÃO: Effect para validação - só executa se NÃO veio do cache
  // =============================================================================================
  useEffect(() => {
    // Se restaurou do cache, IGNORA a validação do valor zero
    if (hasRestoredFromCacheRef.current) {
      return;
    }

    // Só valida se estiver em estado idle e o valor for inválido
    if (paymentState.status === 'idle' && (!valor || valor <= 0)) {
      setPaymentState({
        status: 'failed',
        error: 'Já existe um pagamento em andamento.'
      });
    }
  }, [valor, paymentState.status]);

  // =============================================================================================
  // CORREÇÃO CRÍTICA: Countdown Timer - lógica unificada e precisa
  // =============================================================================================
  useEffect(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    if (paymentState.status === 'pending' || paymentState.status === 'validating') {

      let lastCheckTime = Date.now();
      const TIME_LIMIT = 300; // 5 minutos em segundos

      countdownTimerRef.current = setInterval(() => {
        setPaymentState(prev => {
          if (prev.status !== 'pending' && prev.status !== 'validating') {
            return prev;
          }

          // PRESERVA o timestamp original - não recalcula se já existe
          const originalTimestamp = prev.originalTimestamp || prev.timestamp;
          const currentTime = Date.now();
          const timeElapsed = Math.floor((currentTime - originalTimestamp) / 1000);
          const timeRemaining = Math.max(0, TIME_LIMIT - timeElapsed);

          // Verificação a cada 10 segundos
          if (currentTime - lastCheckTime >= 10000) {
            handleManualCheck();
            lastCheckTime = currentTime;
          }

          // Se tempo realmente acabou
          if (timeRemaining <= 0 && prev.status !== 'expired') {
            handleManualCheck(); // Última verificação
            return {
              ...prev,
              status: 'expired',
              error: 'Tempo para pagamento expirou',
              countdown: 0
            };
          }

          return {
            ...prev,
            countdown: timeRemaining,
            originalTimestamp: originalTimestamp // Preserva o timestamp original
          };
        });
      }, 1000);
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [paymentState.status, paymentState.timestamp]);

  // =============================================================================================
  // Tempo esgotado na tela: cancela o PIX no servidor para o QR Code deixar de
  // aceitar pagamento. Se o cliente pagou no último instante, o servidor
  // credita em vez de cancelar, e o listener do Firestore mostra a confirmação.
  // =============================================================================================
  const expiradoCanceladoRef = useRef<string | null>(null);
  useEffect(() => {
    const transactionId = paymentState.transactionId;
    if (paymentState.status !== 'expired' || !transactionId) return;
    if (expiradoCanceladoRef.current === transactionId) return;
    expiradoCanceladoRef.current = transactionId;

    cancelPaymentFunc({ transactionId, motivo: 'expired' }).catch((error) => {
      console.warn('⚠️ Erro ao cancelar pagamento expirado:', error);
    });
  }, [paymentState.status, paymentState.transactionId]);

  // =============================================================================================
  // Salva estado no cache - lógica simplificada
  // =============================================================================================
  useEffect(() => {
    // Não salva no primeiro render após restaurar do cache
    if (hasRestoredFromCacheRef.current && paymentState.status !== 'idle') {
      hasRestoredFromCacheRef.current = false;
      return;
    }

    const shouldSaveCache = [
      'requesting',
      'pending',
      'validating'
    ].includes(paymentState.status);

    if (shouldSaveCache && paymentState.transactionId) {

      const cacheData: CachedPayment = {
        state: paymentState,
        pixPayload,
        originalValor: effectiveValor,
        merchantInfo,
        timestamp: Date.now()
      };
      saveToCache(userId, cacheData);
    }

    // CORREÇÃO: Só limpa o cache para estados finais específicos
    const shouldClearCache = [
      'completed',
      'failed'
    ].includes(paymentState.status) &&
      paymentState.error !== 'Já existe um pagamento em andamento.';

    if (shouldClearCache) {
      clearCache(userId);
    }
  }, [paymentState, pixPayload, userId, effectiveValor, merchantInfo]);

  // AÇÕES DO USUÁRIO
  const handleCopyToClipboard = () => {
    if (!pixPayload) return;

    navigator.clipboard.writeText(pixPayload).then(() => {
      setIsCopied(true);
      setPaymentState(prev => ({
        ...prev,
        status: 'validating'
      }));
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
      alert('Não foi possível copiar o código.');
    });

    handleManualCheck();
  };

  const handleInitiatePayment = async () => {
    if (paymentState.status !== 'idle') return;

    // CORREÇÃO: Usa effectiveValor que pode vir do cache
    const valorToUse = effectiveValor > 0 ? effectiveValor : valor;

    if (!valorToUse || valorToUse <= 0) {
      console.error('❌ Valor inválido para iniciar pagamento:', valorToUse);
      setPaymentState({
        status: 'failed',
        error: 'Valor inválido para pagamento'
      });
      return;
    }

    setPaymentState({ status: 'requesting' });

    try {
      // Geração do ID mantida no frontend para consistência do fluxo atual
      const transactionId = generateTransactionId(userId);

      const result = await initiatePaymentFunc({
        transactionId: transactionId,
        valor: valorToUse,
        merchantInfo
      });

      // 🟢 ATUALIZAÇÃO CRÍTICA: Agora o sistema é híbrido (Mercado Pago + Santander)
      const { success, error, pixPayload, qrCode, qrCodeBase64, ticketUrl, provider } = result.data as any;

      if (success) {
        const expirationSeconds = 300; // 5 minutos (LOCK_DURATION do backend)

        // 🟢 DIFERENCIA ENTRE MERCADO PAGO E SANTANDER
        let finalPixPayload = pixPayload;

        if (provider === 'mercadopago' && qrCode) {
          // ✅ MERCADO PAGO: Usa o qr_code como pixPayload
          finalPixPayload = qrCode;
          console.log('🔑 Usando QR Code do Mercado Pago como payload');
        }

        if (!finalPixPayload) {
          throw new Error('Não foi possível gerar o código PIX');
        }

        // 🟢 SEMPRE USA O PIX PAYLOAD PARA O QRCodeSVG
        setPixPayload(finalPixPayload);
        setEffectiveValor(valorToUse);
        setPaymentState({
          status: 'pending',
          transactionId: transactionId,
          countdown: expirationSeconds,
          timestamp: Date.now(),
          provider: provider || 'santander', // Define o provedor
          ticketUrl: ticketUrl // Mantém ticketUrl se existir (apenas Mercado Pago)
        });

        console.log(`✅ Pagamento iniciado com ${provider}:`, {
          transactionId,
          provider,
          hasPixPayload: !!finalPixPayload,
          payloadLength: finalPixPayload.length,
          hasTicketUrl: !!ticketUrl
        });

      } else {
        throw new Error(error || 'Sistema ocupado');
      }
    } catch (error) {
      const e = error as FunctionsError;
      console.error('❌ Erro ao iniciar pagamento:', e);
      setPaymentState({
        status: 'failed',
        error: e.message || 'Erro ao iniciar pagamento'
      });
    }
  };

  const handleManualCheck = async () => {
    if (!paymentState.transactionId || paymentState.status !== 'pending') return;
    setPaymentState(prev => ({ ...prev, status: 'validating' }));
    try {
      const result = await checkPaymentFunc({
        transactionId: paymentState.transactionId
      });
      const { status, error } = result.data as any;
      if (status === 'completed') {
        setPaymentState({ status: 'completed', transactionId: paymentState.transactionId });
        onSuccess?.(paymentState.transactionId!);
      } else if (error) {
        setPaymentState(prev => ({ ...prev, status: 'pending', error }));
      } else {
        setPaymentState(prev => ({ ...prev, status: 'pending' }));
      }
    } catch (error) {
      console.error('❌ Erro na verificação manual:', error);
      setPaymentState(prev => ({ ...prev, status: 'pending' }));
    }
  };

  const handleCancelPayment = async () => {
    if (!paymentState.transactionId) return;
    setPaymentState(prev => ({ ...prev, status: 'requesting' }));
    try {
      const result = await cancelPaymentFunc({
        transactionId: paymentState.transactionId
      });
      // Pago no último instante: o servidor creditou em vez de cancelar.
      if ((result.data as { status?: string })?.status === 'completed') {
        setPaymentState({ status: 'completed', transactionId: paymentState.transactionId });
        return;
      }
      clearCache(userId);
      if (onClose) {
        onClose();
      }
    } catch (error: any) {
      console.error('❌ Erro ao cancelar pagamento:', error);
      setPaymentState(prev => ({
        ...prev,
        status: 'pending',
        error: 'Erro ao cancelar pagamento'
      }));
    }
  };

  const handleRetry = () => {
    cleanupCalledRef.current = false;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    clearCache(userId);
    setEffectiveValor(valor);
    setPaymentState({ status: 'idle' });
    setPixPayload('');
    hasRestoredFromCacheRef.current = false;
    window.location.reload();
  };

  const cleanupPayment = useCallback(async () => {
    if (cleanupCalledRef.current) return;
    cleanupCalledRef.current = true;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (paymentStateRef.current.status === 'pending' && paymentStateRef.current.transactionId) {
      try {
        await cancelPaymentFunc({
          transactionId: paymentStateRef.current.transactionId
        });
      } catch (error) {
        console.warn('⚠️ Erro ao cancelar pagamento (cleanup):', error);
      }
    }
  }, [cancelPaymentFunc]);

  useEffect(() => {
    return () => {
      cleanupPayment();
    };
  }, [cleanupPayment]);

  // EFFECTS PARA CONTROLE AUTOMÁTICO (Firestore listener)
  useEffect(() => {
    if (!paymentState.transactionId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'transacoes', paymentState.transactionId),
      (docSnapshot) => {
        if (!docSnapshot.exists()) return;
        const data = docSnapshot.data();
        console.log('📡 Firestore update:', data.status);

        switch (data.status) {
          case 'completed':
            setPaymentState({ status: 'completed', transactionId: paymentState.transactionId });
            onSuccess?.(paymentState.transactionId!);
            break;
          case 'failed':
            setPaymentState({
              status: 'failed',
              transactionId: paymentState.transactionId,
              error: data.errorMessage || 'Pagamento falhou'
            });
            break;
          case 'expired':
            setPaymentState({
              status: 'expired',
              transactionId: paymentState.transactionId,
              error: 'Tempo para pagamento expirou'
            });
            break;
          case 'cancelled':
            // Cancelado em outra aba, ou substituído por um pagamento mais novo:
            // este QR Code não aceita mais pagamento.
            setPaymentState({
              status: 'failed',
              transactionId: paymentState.transactionId,
              error: data.errorMessage || 'Pagamento cancelado'
            });
            break;
        }
      },
      (error) => {
        console.error('❌ Erro no listener:', error);
      }
    );
    return () => unsubscribe();
  }, [paymentState.transactionId, onSuccess]);

  // =============================================================================================
  // RENDERIZAÇÃO - CORRIGIDA
  // =============================================================================================

  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const protectiveStyles = `
  .qr-protective-layer {
    position: relative;
  }
  
  .qr-protective-layer::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 10px,
      rgba(255,255,255,0.1) 10px,
      rgba(255,255,255,0.1) 20px
    );
    pointer-events: none;
    z-index: 1;
  }
  
  .qr-watermark {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: rgba(255,255,255,0.3);
    font-size: 24px;
    font-weight: bold;
    pointer-events: none;
    z-index: 2;
  }
`;


  const renderContent = () => {
    const displayValor = effectiveValor > 0 ? effectiveValor : valor;

    <style>{protectiveStyles}</style>

    // CORREÇÃO: Renderização baseada APENAS no paymentState
    switch (paymentState.status) {
      case 'idle':
        return (
          <div className='text-center p-6'>
            <h3 className='text-xl font-bold mb-4'>Pagar com PIX</h3>
            <p className='text-gray-600 mb-2'>Valor: <strong>{formatarMoeda(displayValor)}</strong></p>
            <p className='text-sm text-gray-500 mb-6'>Clique abaixo para gerar o QR Code</p>
            <button
              onClick={handleInitiatePayment}
              className='w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition-colors'
            >
              Gerar QR Code PIX
            </button>
          </div>
        );

      case 'requesting':
        return (
          <div className='text-center p-8'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4'></div>
            <p>Preparando seu pagamento...</p>
          </div>
        );

      case 'pending':
      case 'validating':
        const isValidating = paymentState.status === 'validating';

        return (
          <div className='text-center p-2'>
            <h3 className='text-xl font-bold mb-2'>Pague com PIX</h3>
            <p className='text-gray-600 mb-2'>Valor: <strong>{formatarMoeda(displayValor)}</strong></p>

            <div className="qr-protective-layer relative mx-auto my-2 w-[256px]">
              <QRCodeSVG
                value={pixPayload}
                size={256}
                level='M'
                includeMargin
              />
              {isValidating && (
                <div className='absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg'>
                  <div className='text-center'>
                    <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2'></div>
                    <p className='text-sm'>Verificando...</p>
                  </div>
                </div>
              )}
            </div>

            <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-2'>
              <p className='text-yellow-800 text-sm'>
                <strong>Tempo limite:</strong> {formatCountdown(paymentState.countdown || 0)}
              </p>
              <p className='text-yellow-700 text-xs mt-1'>
                {paymentState.countdown} segundos restantes
              </p>
            </div>

            <button
              onClick={handleCopyToClipboard}
              className='w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg transition-colors mb-4'
            >
              {isCopied ? 'Copiado! ✔️' : 'Copiar Código PIX'}
            </button>

            <div className='bg-blue-50 border border-blue-200 rounded-lg p-2 mb-2'>
              <p className='text-blue-800 text-md'>
                A confirmação é <strong>automática</strong>. Após pagar, aguarde a confirmação. <br />
                <b className='text-red-500 uppercase'>Jamais faça o pagamento após o tempo limite!!!</b>
              </p>
            </div>

            <button
              onClick={handleManualCheck}
              disabled={isValidating}
              className={`w-full font-bold py-2 px-4 rounded-lg transition-colors ${isValidating
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
            >
              {isValidating ? 'Verificando...' : 'Verificar Pagamento'}
            </button>

            <button
              onClick={handleCancelPayment}
              disabled={isValidating}
              className={`w-full mt-2 font-bold py-2 px-4 rounded-lg transition-colors ${isValidating
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
            >
              Cancelar Pagamento
            </button>
          </div>
        );

      case 'completed':
        return (
          <div className='text-center p-8'>
            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg className='w-8 h-8 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
              </svg>
            </div>
            <h3 className='text-2xl font-bold text-green-600 mb-2'>Pagamento Confirmado!</h3>
            <p className='text-gray-600 mb-6'>Seu pagamento foi processado com sucesso.</p>
            <button
              onClick={() => window.location.reload()}
              className='w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition-colors'
            >
              Fechar
            </button>
          </div>
        );

      case 'failed':
        return (
          <div className='text-center p-8'>
            <div className='w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg className='w-8 h-8 text-orange-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z' />
              </svg>
            </div>
            <h3 className='text-2xl font-bold text-orange-600 mb-2'>Atenção</h3>
            <p className='text-gray-600 mb-4'>
              {paymentState.error}
            </p>
            <button
              onClick={handleRetry}
              className='w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-4 rounded-lg transition-colors'
            >
              Tentar Novamente
            </button>
          </div>
        );

      case 'expired':
        return (
          <div className='text-center p-8'>
            <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg className='w-8 h-8 text-red-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </div>
            <h3 className='text-2xl font-bold text-red-600 mb-2'>Tempo Esgotado</h3>
            <p className='text-gray-600 mb-4'>{paymentState.error}</p>
            <button
              onClick={handleRetry}
              className='w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors mb-2'
            >
              Tentar Novamente
            </button>
            <button
              onClick={onClose}
              className='w-full bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors'
            >
              Fechar
            </button>
          </div>
        );

      default:
        return (
          <div className='text-center p-8'>
            <p>Estado inválido</p>
          </div>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className='rounded-xl shadow-2xl max-w-sm mx-auto overflow-hidden'
    >
      {renderContent()}
    </motion.div>
  );
};

export default PixPayment;